(function () {
  'use strict';

  const CONFIG = {
    enabled: true,
    collectionHandle: 'sale-items',
    discountPercent: 40,
    showCardBadge: false,
    badgeText: '-40%',
    cacheMinutes: 0.25,
    maxPages: 10,
    loadingFailSafeMs: 10000
  };

  if (!CONFIG.enabled || CONFIG.discountPercent <= 0 || CONFIG.discountPercent >= 100) {
    return;
  }

  const productMap = new Map();
  const variantMap = new Map();
  const factor = (100 - CONFIG.discountPercent) / 100;
  const isSaleCollectionPage = new RegExp(
    `(?:^|/)collections/${CONFIG.collectionHandle}(?:/|$)`,
    'i'
  ).test(window.location.pathname);
  let scanTimer = null;
  let loadingFailSafe = null;

  function startCollectionLoadingState() {
    if (!isSaleCollectionPage) return;

    document.documentElement.classList.add('mw-sale-collection', 'mw-sale-loading');

    if (!document.getElementById('mw-sale-loading-style')) {
      const style = document.createElement('style');
      style.id = 'mw-sale-loading-style';
      style.textContent = `
        html.mw-sale-collection.mw-sale-loading
          .grid-products .grid-view-item .priceWraper,
        html.mw-sale-collection
          .grid-products .grid-view-item .priceWraper.mw-sale-pending {
          visibility: hidden !important;
        }
      `;
      (document.head || document.documentElement).appendChild(style);
    }

    loadingFailSafe = window.setTimeout(
      releaseCollectionLoadingState,
      CONFIG.loadingFailSafeMs
    );
  }

  function releaseCollectionLoadingState() {
    window.clearTimeout(loadingFailSafe);
    document.documentElement.classList.remove('mw-sale-loading');
    document.documentElement.classList.add('mw-sale-ready');

    document
      .querySelectorAll('.priceWraper.mw-sale-pending')
      .forEach((element) => element.classList.remove('mw-sale-pending'));
  }

  startCollectionLoadingState();

  function toCents(value) {
    const amount = Number.parseFloat(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  function discounted(cents) {
    return Math.round(cents * factor);
  }

  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.moneyFormat);
      }
    } catch (error) {
      // Use the Intl fallback below.
    }

    const currency =
      (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AED';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-AE', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  function formatRange(min, max, applyDiscount) {
    const first = applyDiscount ? discounted(min) : min;
    const last = applyDiscount ? discounted(max) : max;

    if (first === last) {
      return formatMoney(first);
    }

    return `${formatMoney(first)} – ${formatMoney(last)}`;
  }

  function getHandle(url) {
    if (!url) return null;

    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  function indexProducts(products) {
    products.forEach((product) => {
      const prices = [];
      const variants = [];

      (product.variants || []).forEach((variant) => {
        const price = toCents(variant.price);
        if (price === null) return;

        const indexedVariant = {
          id: String(variant.id),
          price: price,
          handle: product.handle
        };

        prices.push(price);
        variants.push(indexedVariant);
        variantMap.set(indexedVariant.id, indexedVariant);
      });

      if (!prices.length) return;

      productMap.set(product.handle, {
        handle: product.handle,
        min: Math.min(...prices),
        max: Math.max(...prices),
        variants: variants
      });
    });
  }

  function compactProducts(products) {
    return products.map((product) => ({
      handle: product.handle,
      variants: (product.variants || []).map((variant) => ({
        id: variant.id,
        price: variant.price
      }))
    }));
  }

  function cacheKey() {
    const currency =
      (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AED';
    const country = (window.Shopify && Shopify.country) || '';
    return `mw-sale-products-v3:${CONFIG.collectionHandle}:${currency}:${country}`;
  }

  function readCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey()) || 'null');
      const lifetime = CONFIG.cacheMinutes * 60 * 1000;

      if (!cached || !cached.savedAt || Date.now() - cached.savedAt > lifetime) {
        return null;
      }

      return Array.isArray(cached.products) ? cached.products : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(products) {
    try {
      sessionStorage.setItem(
        cacheKey(),
        JSON.stringify({
          savedAt: Date.now(),
          products: compactProducts(products)
        })
      );
    } catch (error) {
      // The storefront still works if browser storage is unavailable.
    }
  }

  async function fetchSaleProducts() {
    const cached = readCache();
    if (cached) return cached;

    const shopRoot =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const products = [];

    for (let page = 1; page <= CONFIG.maxPages; page += 1) {
      const endpoint =
        `${shopRoot}collections/${encodeURIComponent(CONFIG.collectionHandle)}` +
        `/products.json?limit=250&page=${page}&_mw=${Date.now()}`;

      const response = await fetch(endpoint, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Sale collection request failed: ${response.status}`);
      }

      const payload = await response.json();
      const pageProducts = Array.isArray(payload.products) ? payload.products : [];
      products.push(...pageProducts);

      if (pageProducts.length < 250) break;
    }

    writeCache(products);
    return products;
  }

  function addSaleLabel(container, enabled = true) {
    if (!container) return;

    let labels = container.querySelector('.product-labels');
    const existing = labels && labels.querySelector('[data-mw-sale-label]');

    if (!enabled || !String(CONFIG.badgeText || '').trim()) {
      if (existing) existing.remove();
      if (labels && labels.dataset.mwSaleLabels === 'true' && !labels.children.length) {
        labels.remove();
      }
      return;
    }

    if (!labels) {
      labels = document.createElement('span');
      labels.className = 'product-labels fl f-col radius';
      labels.dataset.mwSaleLabels = 'true';
      container.prepend(labels);
    }

    if (existing) {
      existing.textContent = CONFIG.badgeText;
      return;
    }

    const label = document.createElement('span');
    label.className = 'lbl on-sale';
    label.dataset.mwSaleLabel = 'true';
    label.textContent = CONFIG.badgeText;
    labels.prepend(label);
  }

  function applyCard(card) {
    const link = card.querySelector(
      'a.grid-view-item__title[href*="/products/"], a.gimg-link[href*="/products/"]'
    );
    const handle = getHandle(link && link.href);
    const product = handle && productMap.get(handle);
    const priceWrapper = card.querySelector('.priceWraper');

    if (!product || !priceWrapper) {
      if (priceWrapper) priceWrapper.classList.remove('mw-sale-pending');
      return;
    }

    const currentMarker = priceWrapper.querySelector('[data-mw-card-sale]');
    if (currentMarker && currentMarker.dataset.mwCardSale === String(product.min)) {
      priceWrapper.classList.remove('mw-sale-pending');
      addSaleLabel(card.querySelector('.gview-img'), CONFIG.showCardBadge);
      return;
    }

    priceWrapper.innerHTML = `
      <span class="v-hidden">Regular price</span>
      <s class="pr_price mw-regular-price">
        ${formatRange(product.min, product.max, false)}
      </s>
      <span class="v-hidden">Sale price</span>
      <span class="pr_price sale mw-sale-price"
            data-mw-card-sale="${product.min}">
        ${formatRange(product.min, product.max, true)}
      </span>
    `;

    priceWrapper.classList.remove('mw-sale-pending');
    addSaleLabel(card.querySelector('.gview-img'), CONFIG.showCardBadge);
  }

  function markPendingCards(root) {
    if (!isSaleCollectionPage || !root) return;

    const cards = [];

    if (root instanceof Element && root.matches('.grid-view-item')) {
      cards.push(root);
    }

    if (root.querySelectorAll) {
      cards.push(...root.querySelectorAll('.grid-view-item'));
    }

    cards.forEach((card) => {
      const priceWrapper = card.querySelector('.priceWraper');
      if (priceWrapper && !priceWrapper.querySelector('[data-mw-card-sale]')) {
        priceWrapper.classList.add('mw-sale-pending');
      }
    });
  }

  function applyCards(root) {
    if (root instanceof Element && root.matches('.grid-view-item')) {
      applyCard(root);
    }

    (root.querySelectorAll ? root.querySelectorAll('.grid-view-item') : []).forEach(applyCard);
  }

  function addProductLabel(priceWrapper) {
    const productContainer = priceWrapper.closest(
      '.ptContainer, .shopWrapper, .mfp-content, product-info'
    );
    const labels = productContainer && productContainer.querySelector('.product-labels');

    if (labels) {
      addSaleLabel(labels.parentElement);
    }
  }

  function applyProductPrice(priceWrapper, indexedVariant) {
    if (!priceWrapper || !indexedVariant) return;

    const regular = indexedVariant.price;
    const sale = discounted(regular);
    const displayedPrice = priceWrapper.querySelector(
      '[id^="ProductPrice-"], .psinglePrice:not(s)'
    );
    let comparePrice = priceWrapper.querySelector('s.psinglePrice');

    if (!displayedPrice) return;

    const marker = displayedPrice.querySelector('[data-mw-product-sale]');
    if (marker && marker.dataset.mwProductSale === String(sale)) {
      addProductLabel(priceWrapper);
      return;
    }

    const saleContainer = displayedPrice.closest('.psinglePrice') || displayedPrice;
    saleContainer.classList.add('sale');
    displayedPrice.innerHTML =
      `<span data-mw-product-sale="${sale}">${formatMoney(sale)}</span>`;

    if (!comparePrice) {
      comparePrice = document.createElement('s');
      comparePrice.className = 'psinglePrice';
      saleContainer.insertAdjacentElement('afterend', comparePrice);
    }

    comparePrice.classList.remove('hide');
    comparePrice.innerHTML =
      `<span data-mw-product-regular="${regular}">${formatMoney(regular)}</span>`;

    const hiddenLabel = priceWrapper.querySelector('.v-hidden');
    if (hiddenLabel) hiddenLabel.textContent = 'Sale price';

    const discountBadge = priceWrapper.querySelector('.discount-badge');
    if (discountBadge) {
      discountBadge.classList.remove('hide');

      const saveAmount = discountBadge.querySelector('[id^="SaveAmount-"]');
      if (saveAmount) saveAmount.innerHTML = formatMoney(regular - sale);

      const percent = discountBadge.querySelector('.off');
      if (percent) {
        percent.innerHTML = `(<span>${CONFIG.discountPercent}</span>% off)`;
        percent.classList.remove('hide');
      }
    }

    addProductLabel(priceWrapper);
  }

  function findSelectedVariant(priceWrapper) {
    const sectionId = priceWrapper.id.replace(/^price-/, '');
    const form = document.getElementById(`product-form-${sectionId}`);
    const input = form && form.querySelector('.product-variant-id, input[name="id"]');

    if (input && variantMap.has(String(input.value))) {
      return variantMap.get(String(input.value));
    }

    const variantSelector =
      document.getElementById(`variant-selects-${sectionId}`) ||
      priceWrapper.closest('.ptContainer, .shopWrapper, .mfp-content, product-info')
        ?.querySelector('variant-selects');

    if (!variantSelector) return null;

    try {
      const variantScript = variantSelector.querySelector('script[type="application/json"]');
      const variants = JSON.parse((variantScript && variantScript.textContent) || '[]');
      const selectedOptions = Array.from(
        variantSelector.querySelectorAll('select, fieldset'),
        (element) => {
          if (element.tagName === 'SELECT') return element.value;
          return element.querySelector('input:checked')?.value;
        }
      );

      const selected = variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );

      return selected ? variantMap.get(String(selected.id)) || null : null;
    } catch (error) {
      return null;
    }
  }

  function applyProductPrices(root) {
    const wrappers = [];

    if (root instanceof Element && root.matches('.psinglePriceWr[id^="price-"]')) {
      wrappers.push(root);
    }

    if (root.querySelectorAll) {
      wrappers.push(...root.querySelectorAll('.psinglePriceWr[id^="price-"]'));
    }

    wrappers.forEach((priceWrapper) => {
      applyProductPrice(priceWrapper, findSelectedVariant(priceWrapper));
    });
  }

  function scan(root) {
    applyCards(root || document);
    applyProductPrices(root || document);
  }

  function scheduleScan(root) {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(() => scan(root || document), 50);
  }

  function bindDynamicUpdates() {
    document.addEventListener('change', (event) => {
      if (
        event.target.matches(
          'variant-selects input, variant-selects select, .product-variant-id'
        )
      ) {
        scheduleScan(document);
      }
    });

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          const variant = data && data.variant;
          const priceWrapper =
            data && document.getElementById(`price-${data.sectionId}`);
          const indexedVariant = variant && variantMap.get(String(variant.id));
          applyProductPrice(priceWrapper, indexedVariant);
        });
      }
    } catch (error) {
      // The MutationObserver below is the fallback.
    }

    const observer = new MutationObserver((mutations) => {
      const hasNewContent = mutations.some((mutation) => {
        if (!mutation.addedNodes.length) return false;

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) markPendingCards(node);
        });

        return true;
      });

      if (hasNewContent) scheduleScan(document);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', () => scheduleScan(document));
    window.addEventListener('pageshow', () => scheduleScan(document));
  }

  function reconcileThemeUpdates() {
    [150, 400, 800, 1500, 3000, 6000].forEach((delay) => {
      window.setTimeout(() => scan(document), delay);
    });
  }

  async function init() {
    try {
      const products = await fetchSaleProducts();
      indexProducts(products);
      scan(document);
      bindDynamicUpdates();
      reconcileThemeUpdates();
      releaseCollectionLoadingState();
    } catch (error) {
      releaseCollectionLoadingState();
      console.error('[MissWhence sale display]', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
